export function isElectron(): boolean {
  if ((window as any).electronAPI?.isElectron) return true
  if (new URLSearchParams(window.location.search).has('electron')) return true
  return false
}

// Safe accessor — returns undefined if method doesn't exist on older builds
function api(method: string): ((...args: any[]) => any) | undefined {
  const fn = (window as any).electronAPI?.[method]
  return typeof fn === 'function' ? fn.bind((window as any).electronAPI) : undefined
}

export type DesktopSettings = {
  subdomain: string
  useLocal: boolean
  currentUrl: string
}

export async function getDesktopSettings(): Promise<DesktopSettings | null> {
  return api('getDesktopSettings')?.() ?? null
}

export async function setDesktopSettings(
  settings: Partial<Pick<DesktopSettings, 'subdomain' | 'useLocal'>>
): Promise<void> {
  api('setDesktopSettings')?.(settings)
}

export type RawFile = {
  path: string
  name: string
  ext: string
  size: number
  createdAt: string
  modifiedAt: string
}

export type MediaFile = {
  path: string
  name: string
  size: number
  isVideo: boolean
  isImage: boolean
  isAudio: boolean
}

const VIDEO_EXTS = new Set(['.mp4', '.mov', '.avi', '.mkv', '.webm'])
const IMAGE_EXTS = new Set(['.jpg', '.jpeg', '.png', '.gif'])
const AUDIO_EXTS = new Set(['.mp3', '.wav', '.aac', '.flac'])
const MEDIA_EXTS = new Set([...VIDEO_EXTS, ...IMAGE_EXTS, ...AUDIO_EXTS])

export function classifyDroppedFiles(rawFiles: RawFile[]): MediaFile[] {
  return rawFiles
    .filter(f => MEDIA_EXTS.has(f.ext))
    .map(f => ({
      path: f.path,
      name: f.name,
      size: f.size,
      isVideo: VIDEO_EXTS.has(f.ext),
      isImage: IMAGE_EXTS.has(f.ext),
      isAudio: AUDIO_EXTS.has(f.ext),
    }))
}

export type FileChangeEvent = {
  type: string
  path: string
  folder: string
}

export async function processDroppedPaths(paths: string[]): Promise<RawFile[]> {
  return api('processDroppedPaths')?.(paths) ?? []
}

export async function watchFolder(folderPath: string): Promise<boolean> {
  return api('watchFolder')?.(folderPath) ?? false
}

export async function unwatchFolder(folderPath: string): Promise<boolean> {
  return api('unwatchFolder')?.(folderPath) ?? false
}

export async function getWatchedFolders(): Promise<string[]> {
  return api('getWatchedFolders')?.() ?? []
}

export function onFileChanged(callback: (event: FileChangeEvent) => void): (() => void) | null {
  return api('onFileChanged')?.(callback) ?? null
}

// --- Local render export ---

export type SaveVideoResult = { canceled: true } | { filePath: string }

export async function saveVideo(url: string, suggestedName?: string): Promise<SaveVideoResult | null> {
  return api('saveVideo')?.(url, suggestedName) ?? null
}

export function onDownloadProgress(callback: (data: { percent: number }) => void): (() => void) | null {
  return api('onDownloadProgress')?.(callback) ?? null
}

export async function setDefaultExportFolder(): Promise<string | null> {
  return api('setDefaultExportFolder')?.() ?? null
}

export async function getDefaultExportFolder(): Promise<string | null> {
  return api('getDefaultExportFolder')?.() ?? null
}

// --- Native notifications ---

export async function desktopNotify(title: string, body: string): Promise<void> {
  api('notify')?.(title, body)
}

export async function isWindowFocused(): Promise<boolean> {
  return api('isWindowFocused')?.() ?? true
}

export async function notifyIfBackground(title: string, body: string): Promise<void> {
  if (!isElectron()) return
  const focused = await isWindowFocused()
  if (!focused) {
    await desktopNotify(title, body)
  }
}

// --- System tray ---

export type TrayStatus = 'idle' | 'generating' | 'rendering' | 'uploading'

export async function updateTray(status: TrayStatus, projectName?: string): Promise<void> {
  api('updateTray')?.(status, projectName)
}

// --- Multi-window ---

export async function openNewWindow(route?: string): Promise<number | null> {
  return api('openNewWindow')?.(route) ?? null
}

// --- Media extraction (local ffmpeg via bundled binaries) ---

export async function extractFrame(filePath: string, timestampSec?: number): Promise<string | null> {
  return api('extractFrame')?.(filePath, timestampSec ?? 0) ?? null
}

export type ExtractClipResult = { path: string; actualDuration: number }

export async function extractClip(
  filePath: string,
  startSec: number,
  durationSec: number,
): Promise<ExtractClipResult | null> {
  return api('extractClip')?.(filePath, startSec, durationSec) ?? null
}

export async function probeMediaDates(filePaths: string[]): Promise<Record<string, string>> {
  return api('probeMediaDates')?.(filePaths) ?? {}
}

export async function extractAudio(filePath: string): Promise<string | null> {
  return api('extractAudio')?.(filePath) ?? null
}

export type NuxCluster = {
  id: string
  label: string
  folder_name: string
  folder_path: string
  file_count: number
  date_range: string
  earliestDate?: string
  latestDate?: string
  media_types: string[]
  files: { path: string; name: string; type: string; size: number }[]
  cover_thumbnail?: string
}

export type BlockedLibrary = {
  type: 'photos'
  path: string
}

export type ScanFileEntry = { path: string; name: string; ext: string; size: number; createdAt: string; modifiedAt: string }

export type ScanFilesResult = {
  files: ScanFileEntry[]
  blockedPaths: Array<{ path: string; code: string }>
}

export type ScanFilesProgress = {
  filesFound: number
  dirsScanned: number
  totalDirs: number
  currentDir: string
}

export type ScanFilesOpts = {
  maxDepth?: number
  skipDirExts?: string[]
  skipDirNames?: string[]
  fileExts?: string[]
}

export async function scanFiles(dirs: string[], opts?: ScanFilesOpts): Promise<ScanFilesResult | null> {
  const scanFn = api('scanFiles')
  if (!scanFn) return null

  const files: ScanFileEntry[] = []
  const onChunk = api('onScanFilesChunk')
  const unsub = onChunk?.((chunk: ScanFileEntry[]) => {
    files.push(...chunk)
  })

  try {
    const result = await scanFn(dirs, opts)
    return { files, blockedPaths: result?.blockedPaths ?? [] }
  } finally {
    unsub?.()
  }
}

export function onScanFilesProgress(cb: (data: ScanFilesProgress) => void): (() => void) | null {
  return api('onScanFilesProgress')?.(cb) ?? null
}

export async function extractThumbnail(filePath: string, width = 320, seekSec?: number): Promise<string | null> {
  return api('extractThumbnail')?.(filePath, width, seekSec) ?? null
}

export async function batchThumbnails(filePaths: string[], width = 240, seekSec?: number): Promise<Record<string, string | null>> {
  return api('batchThumbnails')?.(filePaths, width, seekSec) ?? {}
}

export async function openFullDiskAccess(): Promise<void> {
  api('openFullDiskAccess')?.()
}

export async function setCliToken(token: string): Promise<void> {
  api('setCliToken')?.(token)
}

export function localMediaUrl(filePath: string): string {
  return `local-media://${encodeURIComponent(filePath)}`
}

// --- Auto-update ---

export function getAppVersion(): string | null {
  if (!isElectron()) return null
  return (window as any).electronAPI.getVersion?.() ?? null
}

export function onUpdateAvailable(cb: (data: { version: string }) => void): (() => void) | null {
  return api('onUpdateAvailable')?.(cb) ?? null
}

export function onUpdateProgress(cb: (data: { percent: number }) => void): (() => void) | null {
  return api('onUpdateProgress')?.(cb) ?? null
}

export function onUpdateDownloaded(cb: (data: { version: string }) => void): (() => void) | null {
  return api('onUpdateDownloaded')?.(cb) ?? null
}

export function onUpdateError(cb: (data: { message: string }) => void): (() => void) | null {
  return api('onUpdateError')?.(cb) ?? null
}

export async function checkForUpdate(): Promise<{ status: string; version?: string } | null> {
  return api('checkForUpdate')?.() ?? null
}

export async function installUpdate(): Promise<void> {
  api('installUpdate')?.()
}

// --- Generic bridge: raw Node APIs ---

export type FsStat = {
  size: number
  isDirectory: boolean
  isFile: boolean
  createdAt: string
  modifiedAt: string
}

export type FsDirEntry = {
  name: string
  isDirectory: boolean
  isFile: boolean
  isSymbolicLink: boolean
}

export type ExecResult = {
  stdout: string
  stderr: string
}

export const bridge = {
  fs: {
    exists: (p: string): Promise<boolean> =>
      api('fs')?.('exists', p) ?? Promise.resolve(false),
    stat: (p: string): Promise<FsStat | null> =>
      api('fs')?.('stat', p) ?? Promise.resolve(null),
    readdir: (p: string): Promise<FsDirEntry[]> =>
      api('fs')?.('readdir', p) ?? Promise.resolve([]),
    readFile: (p: string, encoding?: string): Promise<string | null> =>
      api('fs')?.('readFile', p, encoding) ?? Promise.resolve(null),
    realpath: (p: string): Promise<string | null> =>
      api('fs')?.('realpath', p) ?? Promise.resolve(null),
    access: (p: string): Promise<boolean> =>
      api('fs')?.('access', p) ?? Promise.resolve(false),
  },
  exec: (bin: string, args?: string[], maxBuffer?: number): Promise<ExecResult | null> =>
    api('exec')?.(bin, args, maxBuffer) ?? Promise.resolve(null),
  homedir: (): Promise<string> =>
    api('homedir')?.() ?? Promise.resolve(''),
  tmpdir: (): Promise<string> =>
    api('tmpdir')?.() ?? Promise.resolve(''),
}
